import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { concatenateName, DateFormatter, TimeFormatter } from "@/utils/functions/helperFunctions";
import { TemplateTableSkeletonDesktop, TemplateTableSkeletonMobile } from "@/components/skeletons";
import { useCompactListLayout } from "@/utils/hooks/useCompactListLayout";
import { NO_SEARCH_RESULTS, TABLE_HEADERS, LABELS } from "@/utils/constants/appConstants/commonConstants";
import NoDataAvailable from "@/components/cards/NoDataAvailable";
import { MobileDateField } from "@/components/cards/MobileDateField";
import { MobileListChevron } from "@/components/cards/MobileListChevron";
import TransferOwnershipModal from "@/components/modals/TransferOwnershipModal";
import TransferOwnershipButton from "@/components/buttons/TransferOwnershipButton";
import OwnershipSelectCheckbox from "@/components/buttons/OwnershipSelectCheckbox";
import { transferTemplateOwnership } from "@/services/template.service";
import { invalidateOwnershipTransferListCaches } from "@/utils/listPageCache";
import NoFilterDataCard from "@/components/cards/NoFilterDataCard";
import {
    buildTransferOwnershipPayload,
    type OwnershipSelection,
} from "@/utils/hooks/useOwnershipSelection";
import { successToasterMessage, errorToasterMessage } from "@/utils/functions/toaster";
import { TRANSFER_OWNERSHIP_SUCCESS } from "@/utils/constants/messages";

interface TemplateTableProps {
    templates: any;
    loadingData: boolean;
    setTemplates: any;
    dataLength: number;
    sorting: string;
    isLoading: boolean;
    allFetched?: boolean;
    setSorting: ({ }) => void;
    search: (searchText: string) => void;
    reset: () => void;
    searchItem: string | null;
    title: string;
    dataLoad: boolean;
    resetDataQueries: () => void;
    setPageNo: (pageNo: number) => void;
    id: string;
    selection: OwnershipSelection;
    showTransferActions: boolean;
}

function TemplateTable(props: TemplateTableProps) {
    const navigate = useNavigate();
    const isCompactLayout = useCompactListLayout();
    const [updatedAtStatus, setUpdatedAtStatus] = useState(true);
    const [sortElement, setSortElement] = React.useState("created_at");
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
    const [transferLoading, setTransferLoading] = useState(false);
    const { selection, showTransferActions } = props;

    const lastChangeSort = () => {
        setUpdatedAtStatus(!updatedAtStatus);
        setSortElement("updated_at");
        props.setPageNo(1);
        props.setSorting({ updated_at: !updatedAtStatus ? 1 : -1 });
    };

    const templateIds = (props.templates ?? []).map((template: any) => template.id).filter(Boolean);
    const tableColSpan = showTransferActions ? 9 : 7;
    const isLoadingMore = props.isLoading && !props.dataLoad;

    const handleOpenTransferModal = (template: any) => {
        setTransferLoading(false);
        setSelectedTemplate(template);
        setShowTransferModal(true);
    };

    const handleCloseTransferModal = () => {
        setShowTransferModal(false);
        setSelectedTemplate(null);
    };

    const handleTransferOwnership = async (selectedUser: any) => {
        if (!selectedUser || !selectedTemplate) return;

        setTransferLoading(true);
        try {
            const payload = buildTransferOwnershipPayload(selectedUser);
            await transferTemplateOwnership(selectedTemplate.id, payload);
            successToasterMessage(TRANSFER_OWNERSHIP_SUCCESS);
            handleCloseTransferModal();
            selection.deselect(selectedTemplate.id);
            invalidateOwnershipTransferListCaches("template");
            props.setPageNo(1);
            props.resetDataQueries();
        } catch (error) {
            console.error("Error transferring template ownership:", error);
            errorToasterMessage(error);
        } finally {
            setTransferLoading(false);
        }
    };

    const renderSelectCheckbox = (templateId: string, key: number, suffix = "") => (
        <OwnershipSelectCheckbox
            id={`${props.id}-select-${key}${suffix}`}
            checked={selection.isSelected(templateId)}
            onCheckedChange={() => selection.toggle(templateId)}
            onClick={(event) => event.stopPropagation()}
            aria-label={LABELS.TRANSFER_OWNERSHIP}
        />
    );

    return (
        <>
            {isCompactLayout ? (
                props.templates && !props.dataLoad ? (
                    props.templates?.length > 0 ? (
                        <>
                            <div className="envelope_table">
                                {props.templates?.map((template: any, key: number) => (
                                    <div
                                        className={`envelope_tr gs-mobile-list-card${selection.isSelected(template.id) ? " gs-table-row-selected" : ""}`}
                                        key={template.id}
                                    >
                                        {showTransferActions ? (
                                            <div className="gs-mobile-select-checkbox">
                                                {renderSelectCheckbox(template.id, key, "-mb")}
                                            </div>
                                        ) : (
                                            <div className="gs-mobile-card-icon">
                                                <em className="icon ni ni-template icons" id={`${props.id}-icon-${key}-mb`}></em>
                                            </div>
                                        )}
                                        <div className="env_meta_data gs-mobile-list-card__content w-100" id={`${props.id}-subject-${key}-mb`}>
                                            {template.name}
                                            <div className="text-dark">
                                                <span className="fw-bold">Sent by: </span>
                                                <span className="tb-lead-sub" id={`${props.id}-owner-${key}-mb`}>
                                                    {concatenateName(template.owner?.first_name, template.owner?.last_name)}
                                                </span>
                                            </div>
                                            <div className="text-dark">
                                                <span className="fw-bold">Envelope Count: </span>
                                                <span id={`${props.id}-envelope-count-${key}-mb`}>{template.envelope_count ?? 0}</span>
                                                <span className="fw-bold ms-2">Deleted: </span>
                                                <span id={`${props.id}-deleted-envelope-count-${key}-mb`}>{template.deleted_envelope_count ?? 0}</span>
                                                <span className="fw-bold ms-2">Total: </span>
                                                <span id={`${props.id}-total-envelope-count-${key}-mb`}>{template.total_envelope_count ?? 0}</span>
                                            </div>
                                            <MobileDateField
                                                label="Created At: "
                                                value={template.created_at}
                                                dateId={`${props.id}-created-at-date-${key}-mb`}
                                                timeId={`${props.id}-created-at-time-${key}-mb`}
                                            />
                                            <MobileDateField
                                                label="Updated At: "
                                                value={template.updated_at}
                                                dateId={`${props.id}-updated-at-date-${key}-mb`}
                                                timeId={`${props.id}-updated-at-time-${key}-mb`}
                                            />
                                            {showTransferActions ? (
                                                <div className="gs-mobile-transfer-ownership">
                                                    <TransferOwnershipButton
                                                        id={`template-transfer-ownership-btn-${key}-mb`}
                                                        iconId={`template-transfer-ownership-icon-${key}-mb`}
                                                        onClick={() => handleOpenTransferModal(template)}
                                                    />
                                                </div>
                                            ) : null}
                                        </div>
                                        <MobileListChevron
                                            id={`${props.id}-redirect-${key}-mb`}
                                            onClick={() => navigate(`/template-details/${template.id}`)}
                                        />
                                    </div>
                                ))}
                            </div>
                            {isLoadingMore ? (
                                <TemplateTableSkeletonMobile
                                    rowCount={2}
                                    showActionColumn={showTransferActions}
                                />
                            ) : null}
                        </>
                    ) : props.searchItem ? (
                        <NoDataAvailable>
                            <p id={`${props.id}-no-search-result`}>{NO_SEARCH_RESULTS}</p>
                        </NoDataAvailable>
                    ) : (
                        <NoDataAvailable>
                            <NoFilterDataCard id={props.id} loadAll={props.resetDataQueries} />
                        </NoDataAvailable>
                    )
                ) : (
                    <TemplateTableSkeletonMobile showActionColumn={showTransferActions} />
                )
            ) : (
                <table
                    className={`nk-tb-list nk-tb-ulist table-weight gs-template-table${showTransferActions ? " gs-template-table--with-actions" : ""}`}
                    style={{ tableLayout: "fixed", width: "100%" }}
                >
                    <colgroup>
                        {showTransferActions ? <col className="gs-template-col-select" /> : null}
                        <col className="gs-template-col-name" />
                        <col className="gs-template-col-envelope-count" />
                        <col className="gs-template-col-deleted-count" />
                        <col className="gs-template-col-total-count" />
                        <col className="gs-template-col-owner" />
                        <col className="gs-template-col-created" />
                        <col className="gs-template-col-updated" />
                        {showTransferActions ? <col className="gs-template-col-actions" /> : null}
                    </colgroup>
                    <thead>
                        <tr className="nk-tb-item nk-tb-head">
                            {showTransferActions ? (
                                <th className="nk-tb-col gs-template-col-select align-middle">
                                    <OwnershipSelectCheckbox
                                        id={`${props.id}-select-all`}
                                        checked={
                                            selection.areAllSelected(templateIds)
                                                ? true
                                                : selection.areSomeSelected(templateIds)
                                                  ? "indeterminate"
                                                  : false
                                        }
                                        onCheckedChange={() => selection.toggleAll(templateIds)}
                                        aria-label="Select all templates"
                                    />
                                </th>
                            ) : null}
                            <th className="nk-tb-col gs-template-col-name">
                                <span id={`${props.id}-name-th`}>{TABLE_HEADERS.NAME}</span>
                            </th>
                            <th className="nk-tb-col gs-template-col-envelope-count">
                                <span id={`${props.id}-envelope-count-th`}>{TABLE_HEADERS.ENVELOPE_COUNT}</span>
                            </th>
                            <th className="nk-tb-col gs-template-col-deleted-count">
                                <span id={`${props.id}-deleted-envelope-count-th`}>{TABLE_HEADERS.DELETED_ENVELOPE_COUNT}</span>
                            </th>
                            <th className="nk-tb-col gs-template-col-total-count">
                                <span id={`${props.id}-total-envelope-count-th`}>{TABLE_HEADERS.TOTAL_ENVELOPE_COUNT}</span>
                            </th>
                            <th className="nk-tb-col gs-template-col-owner">
                                <span id={`${props.id}-owner-th`}>{TABLE_HEADERS.OWNER}</span>
                            </th>
                            <th className="nk-tb-col gs-template-col-created">
                                <span id={`${props.id}-created-at-th`}>{TABLE_HEADERS.CREATED_AT}</span>
                            </th>
                            <th className="nk-tb-col gs-template-col-updated" onClick={lastChangeSort}>
                                <span id={`${props.id}-updated-at-th`}>{TABLE_HEADERS.LAST_CHANGED}</span>
                                <em
                                    id={`${props.id}-updated-at-th-sort-icon`}
                                    className={`ml-1 icon ni ${sortElement === "updated_at" ? (updatedAtStatus ? "ni-upword-alt-fill" : "ni-downward-alt-fill") : ""}`}
                                ></em>
                            </th>
                            {showTransferActions ? (
                                <th className="nk-tb-col gs-template-col-actions">
                                    <span id={`${props.id}-actions-th`}>{TABLE_HEADERS.ACTIONS}</span>
                                </th>
                            ) : null}
                        </tr>
                    </thead>
                    {props.templates && !props.dataLoad ? (
                        props.templates?.length > 0 ? (
                            <tbody>
                                {props.templates.map((template: any, key: number) => (
                                    <tr
                                        className={`nk-tb-item${selection.isSelected(template.id) ? " gs-table-row-selected" : ""}`}
                                        key={key}
                                    >
                                        {showTransferActions ? (
                                            <td className="nk-tb-col gs-template-col-select align-middle">
                                                {renderSelectCheckbox(template.id, key)}
                                            </td>
                                        ) : null}
                                        <td className="nk-tb-col gs-template-col-name subject-col">
                                            <Link to={"/template-details/" + template?.id} className="project-title">
                                                <div className="icon mr-2 fs-5 mt-1">
                                                    <em className="icon ni ni-template text-gray"></em>
                                                </div>
                                                <div className="data" style={{ marginTop: 4, maxWidth: "80%", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                                                    <div className="info">
                                                        <div className="text-dark name d-md-none">{template.name}</div>
                                                        <div className="text-dark name tb-col-md">{template.name}</div>
                                                    </div>
                                                </div>
                                            </Link>
                                        </td>
                                        <td className="nk-tb-col gs-template-col-envelope-count tb-col-mb text-left" style={{ maxWidth: "100px" }}>
                                            <div className="meta">
                                                <Link to={`/template/${template.id}/envelopes`} className="link link-primary" id={`${props.id}-envelope-count-${key}`}>
                                                    {template.envelope_count ?? 0}
                                                </Link>
                                            </div>
                                        </td>
                                        <td className="nk-tb-col gs-template-col-deleted-count tb-col-mb text-left" style={{ maxWidth: "100px" }}>
                                            <div className="meta">
                                                <span className="text-dark" id={`${props.id}-deleted-envelope-count-${key}`}>
                                                    {template.deleted_envelope_count ?? 0}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="nk-tb-col gs-template-col-total-count tb-col-mb text-left" style={{ maxWidth: "100px" }}>
                                            <div className="meta">
                                                <span className="text-dark" id={`${props.id}-total-envelope-count-${key}`}>
                                                    {template.total_envelope_count ?? 0}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="nk-tb-col gs-template-col-owner tb-col-mb text-left" style={{ maxWidth: "100px" }}>
                                            <div className="meta">
                                                <span className="text-dark">
                                                    {concatenateName(template.owner?.first_name, template.owner?.last_name)}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="nk-tb-col gs-template-col-created tb-col-mb" style={{ maxWidth: "100px" }}>
                                            <span className="text-dark">
                                                <span>{DateFormatter(template.created_at)}</span>
                                            </span>
                                            <div>
                                                <div className="meta mt-1">
                                                    <span className="version">
                                                        <span className="text-soft">{TimeFormatter(template?.created_at)}</span>
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="nk-tb-col gs-template-col-updated tb-col-mb" style={{ maxWidth: "100px" }}>
                                            <span className="text-dark">
                                                <span>{DateFormatter(template.updated_at)}</span>
                                            </span>
                                            <div>
                                                <div className="meta mt-1">
                                                    <span className="version">
                                                        <span className="text-soft">{TimeFormatter(template?.updated_at)}</span>
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        {showTransferActions ? (
                                            <td className="nk-tb-col gs-template-col-actions">
                                                <TransferOwnershipButton
                                                    id={`template-transfer-ownership-btn-${key}`}
                                                    iconId={`template-transfer-ownership-icon-${key}`}
                                                    onClick={() => handleOpenTransferModal(template)}
                                                />
                                            </td>
                                        ) : null}
                                    </tr>
                                ))}
                                {isLoadingMore ? (
                                    <TemplateTableSkeletonDesktop rowCount={2} showActionColumn={showTransferActions} />
                                ) : null}
                            </tbody>
                        ) : props.searchItem ? (
                            <tbody>
                                <tr>
                                    <td colSpan={tableColSpan}>
                                        <NoDataAvailable>
                                            <p id={`${props.id}-no-search-result`}>{NO_SEARCH_RESULTS}</p>
                                        </NoDataAvailable>
                                    </td>
                                </tr>
                            </tbody>
                        ) : (
                            <tbody>
                                <tr>
                                    <td colSpan={tableColSpan}>
                                        <NoDataAvailable>
                                            <NoFilterDataCard id={props.id} loadAll={props.resetDataQueries} />
                                        </NoDataAvailable>
                                    </td>
                                </tr>
                            </tbody>
                        )
                    ) : (
                        <tbody>
                            <TemplateTableSkeletonDesktop showActionColumn={showTransferActions} />
                        </tbody>
                    )}
                </table>
            )}

            {selectedTemplate && (
                <TransferOwnershipModal
                    show={showTransferModal}
                    onHide={handleCloseTransferModal}
                    onTransferOwnership={handleTransferOwnership}
                    loading={transferLoading}
                    currentOwnerId={selectedTemplate?.owner?.id}
                    title={LABELS.TRANSFER_TEMPLATE_OWNERSHIP}
                    description={LABELS.TRANSFER_OWNERSHIP_DESCRIPTION}
                />
            )}
        </>
    );
}

export default TemplateTable;
